import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContractLedger } from '@wsphere/warranties/domain';
import { firstValueFrom } from 'rxjs';

function toDollars(amount: number) {
  return amount / 100;
}

@Injectable()
export class ContractLedgerWebService {
  constructor(private http: HttpClient) {}

  async retrieve(warrantyId: string): Promise<ContractLedger.RetrieveContractLedgerQueryResponse> {
    const ledger = await firstValueFrom(
      this.http.get<ContractLedger.RetrieveContractLedgerQueryResponse>(`/api/warranties/${warrantyId}/contract-ledger`)
    );

    // Ledger amounts are persisted in the same cents convention as warranty.retail and
    // Payment.amount, not the dollars the ledger's own currency doc describes — convert here so
    // every consumer of this service renders correctly, the same way the rest of the warranty
    // page already divides retail/fee amounts by 100 before formatting them.
    return {
      ...ledger,
      unreconciledSourceEvents: ledger.unreconciledSourceEvents.map(event => ({
        ...event,
        amount: toDollars(event.amount),
      })),
      components: ledger.components.map(component => ({ ...component, amount: toDollars(component.amount) })),
      allocations: ledger.allocations.map(allocation => ({ ...allocation, amount: toDollars(allocation.amount) })),
      funding: ledger.funding.map(entry => ({ ...entry, amount: toDollars(entry.amount) })),
      disbursements: ledger.disbursements.map(disbursement => ({
        ...disbursement,
        amount: toDollars(disbursement.amount),
      })),
      fundingBalance: {
        ...ledger.fundingBalance,
        contractAmountDue: toDollars(ledger.fundingBalance.contractAmountDue),
        netFunding: toDollars(ledger.fundingBalance.netFunding),
        remainingFundingBalance: toDollars(ledger.fundingBalance.remainingFundingBalance),
      },
      partyBalances: {
        ...ledger.partyBalances,
        parties: ledger.partyBalances.parties.map(party => ({
          ...party,
          netAllocation: toDollars(party.netAllocation),
          netDisbursement: toDollars(party.netDisbursement),
          outstandingPayable: toDollars(party.outstandingPayable),
        })),
        customerRefundActivity: toDollars(ledger.partyBalances.customerRefundActivity),
        sellerClawbackReceivable: toDollars(ledger.partyBalances.sellerClawbackReceivable),
      },
    };
  }
}
